import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { adminAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { getCancellationTimeline } from '../utils/cancellationSlabs.js';
import { buildCancellationCommentLines, parseMetaObject } from '../utils/adminCommentHistory.js';
import { formatCommentTimestamp } from '../utils/adminCommentDisplay.js';
import './Registrations.css';

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
}

function formatDateRange(start, end) {
  if (!start && !end) return '—';
  const s = formatDate(start);
  const e = formatDate(end);
  if (!end || s === e) return s;
  return `${s} – ${e}`;
}

function statusOptionsForView() {
  return ['pending', 'on_hold', 'cancelled', 'refunded'];
}

function resolveParticipantRows(detail) {
  const names = Array.isArray(detail?.participantNames) ? detail.participantNames.filter(Boolean) : [];
  if (names.length) return names.map((name, idx) => ({ index: idx + 1, name }));
  const count = Number(detail?.participantCount || 0);
  if (count > 0) {
    return Array.from({ length: count }, (_, idx) => ({ index: idx + 1, name: '-' }));
  }
  return [];
}

function resolveParticipantLabel(row) {
  const direct = String(row?.participantName || '').trim();
  if (direct) return direct;
  const names = Array.isArray(row?.participantNames) ? row.participantNames.filter(Boolean) : [];
  if (names.length) return String(names[0]).trim();
  return '—';
}

function resolveEventBatchCode(row) {
  const meta = row?.meta && typeof row.meta === 'object' ? row.meta : {};
  const metaEvent = meta.event;
  const fromNestedEvent =
    metaEvent && typeof metaEvent === 'object' && metaEvent.code != null
      ? metaEvent.code
      : null;
  const fromMeta =
    meta.event_code ??
    meta.batch_code ??
    meta.code ??
    meta.eventCode ??
    meta.batchCode ??
    fromNestedEvent ??
    null;
  const code = row?.batchCode ?? row?.eventCode ?? row?.event?.code ?? fromMeta ?? null;
  if (code == null || String(code).trim() === '') return '—';
  return String(code).trim();
}

function resolveEventBatchIdForLink(row) {
  const metaRaw = row?.meta?.event_id ?? row?.meta?.eventid;
  const metaBatchId =
    metaRaw != null && String(metaRaw).trim() !== '' ? String(metaRaw).trim() : null;
  const id =
    row?.batchId ?? row?.event?.id ?? row?.registration?.eventId ?? row?.eventId ?? metaBatchId ?? null;
  if (id == null || id === '') return null;
  return String(id);
}

function resolveRegistrationFilterIds(row) {
  const batchId = resolveEventBatchIdForLink(row);
  if (!batchId) return null;
  const trekId = row?.event?.trekId ?? row?.event?.trek?.id ?? null;
  if (!trekId) return null;
  return { trekId: String(trekId), batchId };
}

function buildRegistrationsPageHref(row) {
  const ids = resolveRegistrationFilterIds(row);
  if (!ids) return null;
  const params = new URLSearchParams({
    trekId: ids.trekId,
    batchId: ids.batchId,
    showAll: 'true',
    page: '1',
    limit: '30'
  });
  return `/registrations?${params.toString()}`;
}

function EventRegistrationLink({ row, sx }) {
  const batchCode = resolveEventBatchCode(row);
  const href = buildRegistrationsPageHref(row);
  const label = batchCode;

  if (!href || batchCode === '—') {
    return <span>{label}</span>;
  }

  return (
    <Link
      to={href}
      target="_blank"
      rel="noreferrer"
      style={{
        color: '#2563eb',
        fontWeight: 600,
        textDecoration: 'none',
        ...sx
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
}

function RefundCalculator({ departureDate, totalAmount, refundSlabs, requestedAt }) {
  const timeline = useMemo(
    () =>
      getCancellationTimeline(departureDate, {
        now: requestedAt ? new Date(requestedAt) : new Date(),
        totalAmount: totalAmount != null ? Number(totalAmount) : undefined,
        refundSlabs: Array.isArray(refundSlabs) && refundSlabs.length ? refundSlabs : undefined
      }),
    [departureDate, totalAmount, refundSlabs, requestedAt]
  );

  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: 'grey.50'
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Refund calculator
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Based on package departure date
        {timeline.departureHeading ? `: ${timeline.departureHeading}` : ''}
      </Typography>
      <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }}>
        <Typography variant="body2">{timeline.summaryMessage}</Typography>
      </Alert>
      {timeline.slabs.map((slab, idx) => {
        const isCurrent = idx === timeline.currentIndex;
        return (
          <Box
            key={slab.label}
            sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: isCurrent ? 'warning.main' : 'divider',
              bgcolor: isCurrent ? 'warning.50' : 'background.paper'
            }}
          >
            <Typography variant="body2" fontWeight={isCurrent ? 700 : 500}>
              {isCurrent ? '● ' : ''}{slab.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">{slab.text}</Typography>
            {slab.note ? (
              <Typography variant="caption" display="block" color="error.main">{slab.note}</Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' }
];

export default function Cancellations() {
  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '50',
    status: '',
    search: '',
    sortOrder: 'desc'
  });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 50);
  const status = query.status || '';
  const search = query.search || '';
  const sortOrder = query.sortOrder || 'desc';
  const [viewId, setViewId] = useState(null);
  const [editStatus, setEditStatus] = useState('pending');
  const [newComment, setNewComment] = useState('');
  const [saveError, setSaveError] = useState('');
  const [viewingDocumentId, setViewingDocumentId] = useState(null);
  const [documentError, setDocumentError] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-cancellations', page, limit, status, search, sortOrder],
    queryFn: () => adminAPI.getCancellations({ page, limit, status: status || undefined, search: search || undefined, sortOrder }),
    staleTime: 30_000,
    keepPreviousData: true
  });
  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  const { data: detailRes } = useQuery({
    queryKey: ['admin-cancellation-detail', viewId],
    queryFn: () => adminAPI.getCancellationById(viewId),
    enabled: !!viewId
  });
  const detail = detailRes?.data?.data || null;
  const participantRows = resolveParticipantRows(detail);
  const detailMeta = parseMetaObject(detail?.meta);
  const detailReason = detail?.title || detailMeta.title || '—';
  const detailDescription = detail?.details || detailMeta.description || '—';
  const detailDocument =
    detailMeta.file ||
    detailMeta.document ||
    detail?.attachmentUrl ||
    detail?.attachment_url ||
    null;
  const detailDocumentName =
    detailMeta.file_original_name ||
    detailMeta.fileOriginalName ||
    (detailDocument ? String(detailDocument).split('/').pop() : null);
  const packageDepartureDate = detail?.packageDepartureDate || detail?.packageStartDate || null;

  useEffect(() => {
    if (!detail || !viewId) return;
    setEditStatus(String(detail.status || 'pending'));
    setNewComment('');
    setSaveError('');
    setDocumentError('');
  }, [detail, viewId]);

  const commentLines = useMemo(
    () => (detail ? buildCancellationCommentLines(detail) : []),
    [detail]
  );

  const saveMutation = useMutation({
    mutationFn: ({ id, payload }) => adminAPI.patchCancellation(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['admin-cancellations']);
      queryClient.invalidateQueries(['admin-cancellation-detail', viewId]);
      setSaveError('');
      if (variables?.payload?.newComment) {
        setNewComment('');
      }
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Save failed';
      setSaveError(msg);
    }
  });

  const handleViewDocument = async () => {
    if (!viewId || !detailDocument) return;
    setViewingDocumentId(String(viewId));
    setDocumentError('');
    try {
      const res = await adminAPI.downloadCancellationDocument(viewId);
      const mime = res.headers['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const href = URL.createObjectURL(blob);
      window.open(href, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(href), 60_000);
    } catch (err) {
      setDocumentError(err.response?.data?.message || 'Could not open document.');
    } finally {
      setViewingDocumentId(null);
    }
  };

  const handleCloseDialog = () => {
    setViewId(null);
    setSaveError('');
  };

  const handleSaveCancellation = () => {
    if (!viewId) return;
    setSaveError('');
    const payload = { status: editStatus };
    const trimmed = newComment.trim();
    if (trimmed) payload.newComment = trimmed;
    saveMutation.mutate({ id: viewId, payload });
  };

  return (
    <>
      <PageHeader
        title="Cancellation Requests"
        subtitle="Review participant cancellation requests, refund eligibility, and status updates."
      />

      <FilterBar contentSx={{ py: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
          placeholder="Participant name, id, title, reason…"
          sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 360 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setQuery({ status: e.target.value })}
          sx={{ minWidth: 180 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s.value || 'all'} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
      </FilterBar>

      <Card className="ops-table-card">
          {isLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={32} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading cancellation requests…</Typography>
            </div>
          ) : list.length === 0 ? (
            <EmptyState
              title="No cancellation requests"
              description="Try changing the status filter or search term."
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Id</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Participant</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Event Batch Code</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Participants</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Requested</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{resolveParticipantLabel(r)}</TableCell>
                      <TableCell>
                        <EventRegistrationLink row={r} />
                      </TableCell>
                      <TableCell>{r.participantCount ?? '—'}</TableCell>
                      <TableCell>{r.title || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.requestedAt || r.date || r.createdAt)}</TableCell>
                      <TableCell>
                        <StatusChip status={r.status || 'pending'} label={String(r.status || 'pending')} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" sx={{ borderRadius: 2 }} onClick={() => setViewId(r.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pagination.totalPages > 1 && !isLoading && list.length > 0 ? (
            <div className="ops-table-footer">
              <span className="ops-table-footer-info">Page {page} of {pagination.totalPages}</span>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) })}>Previous</Button>
                <Button size="small" variant="outlined" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) })}>Next</Button>
              </Box>
            </div>
          ) : null}
      </Card>

      <Dialog open={!!viewId} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Cancellation Request</DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  <strong>Requested:</strong> {formatDateTime(detail.requestedAt || detail.date || detail.createdAt)}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.25 }} />
              <Typography variant="body2" fontWeight={700}>Event details</Typography>
              <Typography variant="body2" component="div">
                <strong>Event Batch Code:</strong>{' '}
                <EventRegistrationLink row={detail} sx={{ fontWeight: 500 }} />
              </Typography>
              <Typography variant="body2">
                <strong>Event dates:</strong> {formatDateRange(detail.eventStartDate, detail.eventEndDate)}
              </Typography>
              <Typography variant="body2"><strong>Package:</strong> {detail.packageName || '—'}</Typography>
              <Typography variant="body2">
                <strong>Package dates:</strong> {formatDateRange(detail.packageStartDate, detail.packageEndDate)}
              </Typography>

              <Divider sx={{ my: 0.25 }} />
              <Typography variant="body2"><strong>Reason:</strong> {detailReason}</Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {detailDescription}
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Document:</strong>{' '}
                {detailDocument ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline', textTransform: 'none' }}
                    disabled={!!viewingDocumentId}
                    onClick={handleViewDocument}
                  >
                    {viewingDocumentId ? 'Opening…' : `View${detailDocumentName ? ` (${detailDocumentName})` : ''}`}
                  </Button>
                ) : (
                  '—'
                )}
              </Typography>
              {documentError ? (
                <Alert severity="error" sx={{ py: 0.5 }}>{documentError}</Alert>
              ) : null}

              <RefundCalculator
                departureDate={packageDepartureDate}
                totalAmount={detail.totalAmount}
                refundSlabs={detail.refundSlabs}
                requestedAt={detail.requestedAt || detail.date || detail.createdAt}
              />

              <Typography variant="body2" color="text.secondary">
                Requested by
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                {detail.userName || detailMeta.requester_name || detailMeta.requested_by || '—'}
              </Typography>

              <Divider sx={{ my: 0.5 }} />
              <Typography variant="body2" fontWeight={700}>
                Participants ({detail.participantCount ?? participantRows.length ?? 0})
              </Typography>
              {participantRows.length ? (
                <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 80 }}>#</TableCell>
                      <TableCell>Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {participantRows.map((p) => (
                      <TableRow key={`${p.index}-${p.name}`}>
                        <TableCell>{p.index}</TableCell>
                        <TableCell>{p.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No participant names available.</Typography>
              )}

              <TextField
                select
                size="small"
                label="Status"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                fullWidth
              >
                {statusOptionsForView().map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>

              <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                Comments
              </Typography>
              {commentLines.length > 0 ? (
                <Box
                  sx={{
                    maxHeight: 220,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.25,
                    bgcolor: 'action.hover'
                  }}
                >
                  {commentLines.map((line, i) => {
                    const when = line.when ? formatCommentTimestamp(line.when) : null;
                    return (
                      <Box
                        key={`cancel-comment-${i}`}
                        sx={{
                          mb: i < commentLines.length - 1 ? 1.25 : 0,
                          pb: i < commentLines.length - 1 ? 1.25 : 0,
                          borderBottom: i < commentLines.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        {line.author || when ? (
                          <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
                            {line.author ? (
                              <Typography component="span" variant="body2" fontWeight={600}>
                                {line.author}
                              </Typography>
                            ) : null}
                            {when ? (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: line.author ? 0.75 : 0 }}
                              >
                                {when}
                              </Typography>
                            ) : null}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                          {line.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No comments yet.
                </Typography>
              )}
              <TextField
                size="small"
                label="Add comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                placeholder="New admin note (past comments cannot be edited)"
              />
              {saveError ? (
                <Typography variant="body2" color="error">{saveError}</Typography>
              ) : null}
              <Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveCancellation}
                  disabled={saveMutation.isLoading}
                >
                  {saveMutation.isLoading ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">Loading…</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
